import './OneProject.scss'
import { API_URL } from '../../utils/constants'
import React, { useState, useEffect, useContext } from 'react'
import { useParams } from 'react-router-dom'
import { ProjectsContext } from '../../utils/ProjectsContext'
import DOMPurify from 'dompurify';
import Collapse from '../../components/Collapse/Collapse';
import ImageFocus from '../../components/ImageFocus/ImageFocus';
 
function OneProject() {

    const { slug } = useParams();
    const [project, setProject] = useState({});
    const { setDisplayHeader, displayHeader } = useContext(ProjectsContext);
    
    const [imageFocusUrl, setImageFocusUrl]=useState('');
    const [displayImageFocus, setDisplayImageFocus]= useState(false);

    const cleanedAboutShow = DOMPurify.sanitize(project.aboutShow);
    const cleanedAboutSceno = DOMPurify.sanitize(project.aboutSceno);

    /* -------------------------
       MEDIA HELPERS
    ------------------------- */

    function getMediaType(media) {
        const fileType = media?.fileType || '';

        if (fileType.startsWith('video/')) return 'video';
        if (fileType.startsWith('image/')) return 'image';
        if (media?.imageUrl) return 'image'; // legacy images
        return null;
    }

    function renderProjectMedia(media, className = '', alt = '') {

        if (!media?.imageUrl) return null;

        const mediaType = getMediaType(media);

        if (mediaType === 'video') {
            return (
                <video
                    className={className}
                    src={media.imageUrl}
                    muted
                    autoPlay
                    loop
                    playsInline
                    preload="metadata"
                />
            );
        }

        if (mediaType === 'image') {
            return (
                <img
                    className={className}
                    src={media.imageUrl}
                    alt={alt}
                />
            );
        }

        return null;
    }

    /* -------------------------
       FETCH PROJECT
    ------------------------- */

    useEffect(() => {
        fetch(`${API_URL}/api/projects/${slug}`)
            .then((res) => res.json())
            .then((data) => {
                window.scrollTo(0, 0);
                setProject(data);
            })
            .catch((error) => console.log(error.message));
    }, [slug]);

    const prodTypeArray = Array.from(
        new Set(project.productionList?.map(prod => prod.prodType) || [])
    );

    return  (      
        <main
            onClick={()=>displayHeader===true && setDisplayHeader(false)}
            className='oneProject'
        >
            
            {/* TITRE */}
            <div className='oneProject_titleContainer'>
                <div className='oneProject_titleContainer_title'>
                    <h4 translate="no">{project.title}</h4>
                    <p translate="no">{project.subtitle}</p>
                </div>

                <p className='oneProject_titleContainer_projectInfos'>
                    {project.projectInfos}
                </p>
            </div>

            {/* MEDIA 1 */}
            {project.projectImages?.length > 0 && 
            <div className='oneProject_firstImageContainer'>
                {renderProjectMedia(
                    project.projectImages[0],
                    '',
                    `média 1 du projet ${project.title}`
                )}
            </div>
            }
            
            {/* INFOS */}
            <section className='oneProject_firstInfosBlock'>

                {cleanedAboutShow && cleanedAboutSceno &&
                <div className='oneProject_firstInfosBlock_showAndSceno'>

                    {cleanedAboutShow && 
                    <div className='oneProject_firstInfosBlock_showAndSceno_showBlock'>
                        <h5>
                            {project.projectType==='spectacle vivant'
                                ? 'La pièce'
                                : (project.projectType==='évènement'
                                    ? "L'évènement"
                                    : 'Le contexte')}
                        </h5>

                        <p dangerouslySetInnerHTML={{__html:cleanedAboutShow}}></p>
                    </div>
                    }

                    {cleanedAboutSceno && 
                    <div className='oneProject_firstInfosBlock_showAndSceno_scenoBlock'>

                        {(project.projectType==='spectacle vivant'
                            || project.projectType==='évènement') &&
                            <h5>La scénographie</h5>
                        }

                        <p dangerouslySetInnerHTML={{__html:cleanedAboutSceno}}></p>
                    </div>
                    }

                </div>
                }

                {/* DISTRIBUTION */}
                <div className='oneProject_firstInfosBlock_distributionAndLinks'>

                    {project.artistsList?.length > 0 &&
                    <div className='oneProject_firstInfosBlock_distributionAndLinks_distribution'>

                        <h5>
                            {project.projectType==='spectacle vivant'
                                ? 'Distribution'
                                : "L'équipe"}
                        </h5>

                        <ul className='oneProject_firstInfosBlock_distributionAndLinks_distribution_list'>
                            {project.artistsList.map((artist)=>(
                            <li key={artist._id}>
                                <p>
                                    <span>{artist.artistFunction}</span>
                                    {artist.artistName}
                                </p>
                            </li>
                            ))}
                        </ul>

                    </div>
                    }

                    {project.links?.length > 0 &&
                    <ul className='oneProject_firstInfosBlock_distributionAndLinks_links'>

                        {project.links.map((link) => (
                        <li key={link._id}>
                            <a
                                href={link.linkUrl}
                                target='_blank'
                                rel='noreferrer'
                                aria-label={`Accéder au lien ${link.linkName}`}
                            >
                                {link.linkName}
                            </a>
                        </li>
                        ))}

                    </ul>
                    }

                </div>

            </section>

            {/* MEDIA 2 à 4 */}

            {project.projectImages?.length > 1 && 
            <section className={`oneProject_secondImageContainer_${project.projectImages.length}`}>

                {project.projectImages.slice(1, 4).map((media, index) => (
                    <React.Fragment key={media._id || media.imageUrl || index}>
                        {renderProjectMedia(
                            media,
                            `oneProject_secondImageContainer_${project.projectImages.length}_img_${index}`,
                            `média ${index + 2} du projet ${project.title}`
                        )}
                    </React.Fragment>
                ))}

            </section>
            }

            {/* PRESSE */}

            {project.press?.length > 0 && 
            <section className='oneProject_pressBlocks'>

                <ul className='oneProject_pressBlocks_container'>

                    {project.press.map((press)=>(
                    <li key={press._id}>

                        <h6>{press.mediaName}</h6>

                        <p>{press.quote}</p>

                        <a
                            href={press.mediaLink}
                            target='_blank'
                            rel='noreferrer'
                        >
                            lien vers l'article
                        </a>

                    </li>
                    ))}

                </ul>

            </section>
            }

            {/* MEDIA 5 à 15 */}

            {project.projectImages?.length > 4 &&
            project.projectImages?.length <= 15 &&

            <section className={`oneProject_thirdImageContainer oneProject_thirdImageContainer_${project.projectImages.length}`}> 

                {project.projectImages.slice(4, 15).map((media, index) => (

                    <React.Fragment key={media._id || media.imageUrl || index}>

                        {renderProjectMedia(
                            media,
                            `oneProject_thirdImageContainer_${project.projectImages.length}_img_${index}`,
                            `média ${index + 5} du projet ${project.title}`
                        )}

                    </React.Fragment>

                ))}

            </section>}

            {/* PRODUCTION */}

            {project.productionList?.length > 0 &&    
            <Collapse title="Production" style='white'>

                <div className='oneProject_productionBlocks'>

                    <div className='oneProject_productionBlocks_container'>

                        {prodTypeArray.map((prodType, index) => (

                            <div
                                key={prodType}
                                className={
                                    index % 2 === 0
                                    ? 'oneProject_productionBlocks_container_block oneProject_productionBlocks_container_block--leftText'
                                    : 'oneProject_productionBlocks_container_block oneProject_productionBlocks_container_block--rightText'
                                }
                            >

                                <h6>{prodType}</h6>

                                <ul>

                                    {project.productionList
                                    ?.filter(prodFiltered => prodFiltered.prodType === prodType)
                                    .map((prod) => (

                                        <li key={prod._id}>

                                            <a href={prod.prodLink}>
                                                {prod.prodName}
                                            </a>

                                            <span>{prod.prodInfos}</span>

                                        </li>

                                    ))}

                                </ul>

                            </div>

                        ))}

                    </div>

                </div>

            </Collapse>
            }

            {/* DIFFUSION */}

            {project.diffusionList?.length > 0 &&
            <Collapse title="Lieux de diffusion" style='light'>

                <div className='oneProject_diffBlock'>

                    <ul>

                        {project.diffusionList.map((diff)=>(

                            <li key={diff._id}>

                                <a
                                    href={diff.placeLink}
                                    target='_blank'
                                    rel='noreferrer'
                                >
                                    {diff.placeName}
                                </a>

                                <span>
                                    {diff.city} {diff.dates}
                                </span>

                            </li>

                        ))}

                    </ul>

                </div>

            </Collapse>
            }

        </main>
    )
}

export default OneProject